import {
  RunLibraryBackendDto,
  RunLibraryNGSBackendDto,
  RunLibraryMicroarrayBackendDto,
  RunLibraryMLPABackendDto,
  RunLibraryTripletBackendDto
} from './sequencing-run-backend.dto';
import {
  RunLibraryAssignment,
  RunLibrarySpecimen,
  NgsRunLibraryAssignment,
  MicroarrayRunLibraryAssignment,
  MlpaRunLibraryAssignment,
  TripletExpansionRunLibraryAssignment
} from '@model/sequencing/run-library-assignment.model';

/**
 * Factory for creating technology-specific RunLibraryAssignment instances.
 *
 * Follows the Factory Method pattern to encapsulate the creation logic
 * and support Open/Closed Principle - new technologies can be added
 * by adding new case statements without modifying existing code structure.
 *
 * This factory maps backend DTOs to the polymorphic domain model,
 * ensuring type safety through TypeScript's discriminated unions.
 *
 * Architecture Layer: Infrastructure
 * - Maps from Backend DTO to Domain Model
 * - Located in infrastructure because it depends on backend DTO structure
 */
export class RunLibraryAssignmentFactory {
  /**
   * Creates the appropriate RunLibraryAssignment based on the technology discriminator.
   * Uses the 'technology' field from the backend as the type discriminator.
   *
   * @param dto The backend DTO containing library assignment data
   * @returns The technology-specific domain model
   */
  static create(dto: RunLibraryBackendDto): RunLibraryAssignment {
    const specimens = this.mapSpecimens(dto);

    // TypeScript narrows the type based on technology discriminant
    switch (dto.technology) {
      case 'NGS':
        return this.createNgs(dto, specimens);
      case 'MICROARREGLOS':
        return this.createMicroarray(dto, specimens);
      case 'MLPA':
        return this.createMlpa(dto, specimens);
      case 'EXPANSION_TRIPLETAS':
        return this.createTripletExpansion(dto, specimens);
      default: {
        // Exhaustive check - TypeScript ensures all cases are handled
        const _exhaustiveCheck: never = dto;
        // Default to NGS for unknown technologies (shouldn't happen with proper typing)
        return this.createNgs(
          _exhaustiveCheck as RunLibraryNGSBackendDto,
          specimens
        );
      }
    }
  }

  /**
   * Creates multiple RunLibraryAssignment instances from a list of backend DTOs.
   *
   * @param dtos Array of backend DTOs
   * @returns Array of technology-specific domain models
   */
  static createList(dtos: RunLibraryBackendDto[]): RunLibraryAssignment[] {
    return dtos.map((dto) => this.create(dto));
  }

  private static mapSpecimens(dto: RunLibraryBackendDto): RunLibrarySpecimen[] {
    if (!dto.specimens) {
      return [];
    }
    return dto.specimens.map((spec) => ({
      libSpecimenId: spec.libSpecimenId,
      specimenId: spec.specimenId,
      indexAdapterId: spec.indexAdapterId,
      indexValue: spec.indexValue,
      lgCode: spec.lgCode,
      assignmentDate: spec.assignmentDate
    }));
  }

  private static buildBaseFields(
    dto: RunLibraryBackendDto,
    specimens: RunLibrarySpecimen[]
  ) {
    return {
      runLibId: dto.runLibId,
      libraryId: dto.libraryId,
      libraryCode: dto.libraryCode,
      replicaColumn: dto.replicaColumn,
      lineNumber: dto.lineNumber,
      assignmentDate: dto.assignmentDate,
      specimens,
      createdAt: dto.createdAt || dto.assignmentDate
    };
  }

  private static createNgs(
    dto: RunLibraryNGSBackendDto,
    specimens: RunLibrarySpecimen[]
  ): NgsRunLibraryAssignment {
    return { technology: 'NGS', ...this.buildBaseFields(dto, specimens) };
  }

  private static createMicroarray(
    dto: RunLibraryMicroarrayBackendDto,
    specimens: RunLibrarySpecimen[]
  ): MicroarrayRunLibraryAssignment {
    return {
      technology: 'MICROARREGLOS',
      ...this.buildBaseFields(dto, specimens),
      chipType: dto.chipType ?? '',
      arrangementType: dto.arrangementType ?? '',
      plateName: dto.plateName ?? null,
      chipLot: dto.chipLot ?? null,
      chipReference: dto.chipReference ?? null
    };
  }

  private static createMlpa(
    dto: RunLibraryMLPABackendDto,
    specimens: RunLibrarySpecimen[]
  ): MlpaRunLibraryAssignment {
    return {
      technology: 'MLPA',
      ...this.buildBaseFields(dto, specimens),
      equipment: dto.equipment ?? null,
      plateName: dto.plateName ?? null,
      creationDate: dto.creationDate ?? null,
      batchLot: dto.batchLot ?? dto.chipLot ?? null,
      batchReference: dto.batchReference ?? dto.chipReference ?? null
    };
  }

  private static createTripletExpansion(
    dto: RunLibraryTripletBackendDto,
    specimens: RunLibrarySpecimen[]
  ): TripletExpansionRunLibraryAssignment {
    return {
      technology: 'EXPANSION_TRIPLETAS',
      ...this.buildBaseFields(dto, specimens),
      equipment: dto.equipment ?? null,
      plateName: dto.plateName ?? null,
      creationDate: dto.creationDate ?? null,
      batchLot: dto.batchLot ?? dto.chipLot ?? null,
      batchReference: dto.batchReference ?? dto.chipReference ?? null
    };
  }
}
